/** Sidebar nav targets highlighted during the dashboard tour. */
import {
  renderDashboardNavIconSvg,
  type DashboardNavIconName,
} from '@/helpers/dashboardNavIcons'

export const NAV_TOUR_BASE_STEP_IDS = [
  'dashboard',
  'donations',
  'statements',
  'account',
  'support',
] as const

export type NavTourBaseStepId = (typeof NAV_TOUR_BASE_STEP_IDS)[number]
export type NavTourStepId =
  | NavTourBaseStepId
  | 'upgrade'
  | 'menu'
  | 'claim-tax-back'

export const NAV_TOUR_MOBILE_MENU_STEP_ID = 'menu' as const

/** Matches the dashboard sidebar mobile breakpoint in `_dashboard.scss`. */
export const NAV_TOUR_MOBILE_BREAKPOINT_PX = 860

/** Sidebar slide-in duration in `_dashboard.scss` (0.24s) plus a small buffer. */
export const NAV_TOUR_MOBILE_SIDEBAR_OPEN_MS = 300

export function getNavTourStepIds(
  isPaid: boolean,
  options?: { includeMobileMenu?: boolean; includeClaimTaxBack?: boolean }
): NavTourStepId[] {
  const navSteps: NavTourStepId[] = isPaid
    ? [...NAV_TOUR_BASE_STEP_IDS]
    : [...NAV_TOUR_BASE_STEP_IDS, 'upgrade']

  const withClaim = options?.includeClaimTaxBack
    ? insertAfter(navSteps, 'donations', 'claim-tax-back')
    : navSteps

  if (options?.includeMobileMenu) {
    return [NAV_TOUR_MOBILE_MENU_STEP_ID, ...withClaim]
  }

  return withClaim
}

function insertAfter(
  steps: NavTourStepId[],
  afterId: NavTourStepId,
  insertId: NavTourStepId
): NavTourStepId[] {
  const index = steps.indexOf(afterId)
  if (index === -1) return [...steps, insertId]
  return [...steps.slice(0, index + 1), insertId, ...steps.slice(index + 1)]
}

export function isNavTourMenuStep(stepId: NavTourStepId): boolean {
  return stepId === NAV_TOUR_MOBILE_MENU_STEP_ID
}

export function isNavTourFirstStep(
  stepId: NavTourStepId,
  isMobile: boolean
): boolean {
  if (isMobile) {
    return isNavTourMenuStep(stepId)
  }
  return stepId === 'dashboard'
}

export function isNavTourMobileViewport(): boolean {
  return window.matchMedia(`(max-width: ${NAV_TOUR_MOBILE_BREAKPOINT_PX}px)`)
    .matches
}

export function waitForNavTourSidebar(): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, NAV_TOUR_MOBILE_SIDEBAR_OPEN_MS)
  })
}

export function navTourSelector(stepId: NavTourStepId): string {
  return `[data-tour-nav="${stepId}"]`
}

const NAV_TOUR_STEP_ICONS: Partial<
  Record<NavTourStepId, DashboardNavIconName>
> = {
  menu: 'menu',
  dashboard: 'dashboard',
  donations: 'donations',
  'claim-tax-back': 'claim',
  statements: 'statements',
  account: 'settings',
  support: 'support',
}

export function getNavTourStepIcon(
  stepId: NavTourStepId
): DashboardNavIconName | null {
  return NAV_TOUR_STEP_ICONS[stepId] ?? null
}

function getTourPopoverTitleText(titleEl: HTMLElement): string {
  const textEl = titleEl.querySelector('.sa-driver-popover-title-text')
  if (textEl?.textContent) {
    return textEl.textContent.trim()
  }
  return titleEl.textContent?.trim() ?? ''
}

/** Adds the matching sidebar icon to the left of a Driver.js popover title. */
export function prependNavIconToPopoverTitle(
  titleEl: HTMLElement,
  iconName: DashboardNavIconName
): void {
  if (titleEl.querySelector('.sa-driver-popover-title-icon')) {
    return
  }

  const titleText = getTourPopoverTitleText(titleEl)
  titleEl.textContent = ''

  const iconSpan = document.createElement('span')
  iconSpan.className = 'sa-driver-popover-title-icon'
  iconSpan.innerHTML = renderDashboardNavIconSvg(iconName)

  const textSpan = document.createElement('span')
  textSpan.className = 'sa-driver-popover-title-text'
  textSpan.textContent = titleText

  titleEl.append(iconSpan, textSpan)
  titleEl.classList.add('driver-popover-title--with-icon')

  titleEl.style.display = 'inline-flex'
  titleEl.style.alignItems = 'center'
  titleEl.style.gap = '10px'

  const iconSvg = iconSpan.querySelector('svg')
  if (iconSvg) {
    iconSvg.style.width = '20px'
    iconSvg.style.height = '20px'
    iconSvg.style.flexShrink = '0'
  }
}

const PAID_PLAN_STAR_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>'

/** Adds the paid-plan star badge to the right of a Driver.js popover title. */
export function appendPaidPlanStarToPopoverTitle(titleEl: HTMLElement): void {
  if (titleEl.querySelector('.sa-paid-badge')) {
    return
  }

  const titleText = getTourPopoverTitleText(titleEl)
  const iconEl = titleEl.querySelector('.sa-driver-popover-title-icon')
  titleEl.textContent = ''

  if (iconEl) {
    titleEl.appendChild(iconEl)
  }

  const textSpan = document.createElement('span')
  textSpan.className = 'sa-driver-popover-title-text'
  textSpan.textContent = titleText

  const badge = document.createElement('span')
  badge.className = 'sa-paid-badge sa-paid-badge--tour-title'
  badge.innerHTML = PAID_PLAN_STAR_SVG
  titleEl.append(textSpan, badge)
  titleEl.classList.add('driver-popover-title--with-star')
  if (iconEl) {
    titleEl.classList.add('driver-popover-title--with-icon')
  }

  // Enforce spacing/size in case Driver.js default styles override class rules.
  titleEl.style.display = 'inline-flex'
  titleEl.style.alignItems = 'center'
  titleEl.style.gap = '5px'

  badge.style.width = '17px'
  badge.style.height = '17px'
  badge.style.marginLeft = '0'
  badge.style.flexShrink = '0'

  const badgeSvg = badge.querySelector('svg')
  if (badgeSvg) {
    badgeSvg.style.width = '11px'
    badgeSvg.style.height = '11px'
  }
}

/** Adds an upgrade plan chip below a step description when a feature is locked. */
export function appendUpgradePlanChip(
  descriptionEl: HTMLElement,
  label: string
): void {
  if (descriptionEl.querySelector('.sa-driver-plan-chip')) {
    return
  }

  const chip = document.createElement('p')
  chip.className = 'sa-driver-plan-chip'
  chip.innerHTML = `<span class="sa-paid-badge sa-paid-badge--tour-chip">${PAID_PLAN_STAR_SVG}</span><span>${label}</span>`
  descriptionEl.appendChild(chip)
}

/** Adds a first-step note that menu navigation is paused during the tour. */
export function appendTourNavigationPausedHint(
  descriptionEl: HTMLElement,
  message: string
): void {
  if (descriptionEl.querySelector('.sa-driver-nav-hint')) {
    return
  }

  const hint = document.createElement('p')
  hint.className = 'sa-driver-nav-hint'
  hint.setAttribute('role', 'note')
  hint.textContent = message
  descriptionEl.appendChild(hint)
}

type DriverPopoverDom = {
  wrapper: HTMLElement
  arrow: HTMLElement
}

/**
 * Pins the Driver.js popover as a full-width bottom sheet on mobile.
 * Avoids squashing beside the open sidebar.
 */
export function pinMobileTourPopover(popover: DriverPopoverDom): void {
  const sheet = popover.wrapper
  sheet.style.setProperty('position', 'fixed', 'important')
  sheet.style.setProperty('left', '16px', 'important')
  sheet.style.setProperty('right', '16px', 'important')
  sheet.style.setProperty(
    'bottom',
    'max(16px, env(safe-area-inset-bottom, 0px))',
    'important'
  )
  sheet.style.setProperty('top', 'auto', 'important')
  sheet.style.setProperty('width', 'auto', 'important')
  sheet.style.setProperty('min-width', '0', 'important')
  sheet.style.setProperty('max-width', 'none', 'important')
  sheet.style.setProperty('margin', '0', 'important')
  popover.arrow.style.display = 'none'
}
